/** Helper to check if value is empty */
const isEmpty = (value: unknown): boolean => {
	if (value === null || value === undefined) return true;
	if (typeof value === 'string' && value.trim() === '') return true;
	if (Array.isArray(value) && value.length === 0) return true;
	if (typeof value === 'object' && Object.keys(value).length === 0) return true;
	return false;
};

/**
 * Format tag data for Garfield
 *
 * Converts various data types into Garfield-compatible format
 *
 * @param tagData - The data to format
 * @returns Formatted tag data ('NA' for empty, joined string for arrays, or original value)
 */
export const formatTagsData = (tagData: unknown): unknown => {
	if (isEmpty(tagData)) {
		return 'NA';
	}

	if (typeof tagData !== 'object') {
		return tagData;
	}

	if (Array.isArray(tagData)) {
		return tagData.join('|');
	}

	return tagData;
};

/**
 * Get tags data from event data
 *
 * Transforms event data into Garfield tag format (t1, t2, t3, etc.)
 *
 * @param tagsData - The data to transform into tags
 * @param tagsList - Map of field names to tag numbers
 * @returns Object with tag keys (t1, t2, etc.) and formatted values
 *
 */
export const getTagsData = <T extends Record<string, number>>(
	tagsData: Partial<Record<keyof T, unknown>>,
	tagsList: T,
): Partial<Record<`t${number}`, unknown>> => {
	type U = T[keyof T];
	const payload: Partial<Record<`t${U}`, unknown>> = {};
	const extraDataKeys = Object.keys(tagsData) as (keyof T)[];
	extraDataKeys.forEach((key) => {
		if (key in tagsList) {
			payload[`t${tagsList[key]}`] = formatTagsData(tagsData[key]);
		}
	});
	return payload;
};
